import { useEffect, useRef, useState } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { iqeAgentApiRef } from '../../../../../apis/iqeAgentApi';
import { notificationApiRef } from '../../../../../apis/notificationApi';
import { toRunLogFiles } from '../dto/responseDto/runResults.response.dto';
import { downloadRunLogs, getRunLogFileNames } from '../utils/run-log-download';
import type { RunLogFile } from '../interfaces/ui';

interface RunLogsDialogState {
  runId: string;
  files: RunLogFile[];
  fileNames: string[];
}

export function useRunLogs(apmNumber: string, projectId: string) {
  const api = useApi(iqeAgentApiRef);
  const notifications = useApi(notificationApiRef);
  const [dialog, setDialog] = useState<RunLogsDialogState | null>(null);
  const [selectedIndices, setSelectedIndices] = useState<number[]>([]);
  const [pendingRunId, setPendingRunId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const generation = useRef(0);
  const busy = useRef(false);

  useEffect(() => {
    generation.current += 1;
    busy.current = false;
    setDialog(null);
    setSelectedIndices([]);
    setPendingRunId(null);
    setSaving(false);
    return () => {
      generation.current += 1;
    };
  }, [apmNumber, projectId]);

  const notifyError = (error: unknown) =>
    notifications.sendNotification({
      message:
        error instanceof Error ? error.message : 'Unable to download run logs.',
      severity: 'error',
      disapperAfterMs: 4000,
    });

  const closeDialog = () => {
    if (busy.current) return;
    setDialog(null);
    setSelectedIndices([]);
  };

  const fetchLogs = async (runId: string) => {
    if (busy.current || !apmNumber || !projectId || !runId) return;
    busy.current = true;
    const currentGeneration = ++generation.current;
    setPendingRunId(runId);
    try {
      const response = await api.getIqeRunLogs({ apmNumber, projectId, runId });
      if (generation.current !== currentGeneration) return;
      const files = toRunLogFiles(response);
      if (!files.length) {
        notifications.sendNotification({
          message: 'No log files are available for this run.',
          severity: 'info',
          disapperAfterMs: 4000,
        });
      } else if (files.length === 1) {
        await downloadRunLogs(runId, files);
      } else {
        setSelectedIndices([]);
        setDialog({
          runId,
          files,
          fileNames: getRunLogFileNames(runId, files),
        });
      }
    } catch (error) {
      if (generation.current === currentGeneration) notifyError(error);
    } finally {
      if (generation.current === currentGeneration) {
        busy.current = false;
        setPendingRunId(null);
      }
    }
  };

  const toggleFile = (index: number) => {
    if (busy.current) return;
    setSelectedIndices(current =>
      current.includes(index)
        ? current.filter(selected => selected !== index)
        : [...current, index],
    );
  };

  const downloadFiles = async (all: boolean) => {
    if (!dialog || busy.current) return;
    const indices = dialog.files
      .map((_, index) => index)
      .filter(index => all || selectedIndices.includes(index));
    if (!indices.length) return;
    busy.current = true;
    setSaving(true);
    const currentGeneration = generation.current;
    try {
      await downloadRunLogs(
        dialog.runId,
        indices.map(index => dialog.files[index]),
        indices.map(index => dialog.fileNames[index]),
      );
      if (generation.current === currentGeneration) {
        setDialog(null);
        setSelectedIndices([]);
      }
    } catch (error) {
      if (generation.current === currentGeneration) notifyError(error);
    } finally {
      if (generation.current === currentGeneration) {
        busy.current = false;
        setSaving(false);
      }
    }
  };

  return {
    dialog,
    selectedIndices,
    pendingRunId,
    saving,
    fetchLogs,
    toggleFile,
    downloadFiles,
    closeDialog,
  };
}
